import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { REGISTER_INSTITUTE_MODE } from "@features/RegisterInstitute/constants/constants";

export interface RegisterInstituteFormState {
	formData: Record<string, unknown>;
	currentStep: number;
	currentSubStep: number;
	maxReachedStep: number;
	maxReachedSubStep: number;
	lastSaved: string | null;
	isDirty: boolean;
	registerInstituteMode: (typeof REGISTER_INSTITUTE_MODE)[keyof typeof REGISTER_INSTITUTE_MODE] | null;
	isSameAsPrincipal: boolean;
	selectedLocationOptions?: {
		nationalAssembly?: { value: string; label: string; uuid?: string };
		provinceAssembly?: { value: string; label: string; uuid?: string };
		unionCouncil?: { value: string; label: string; uuid?: string };
	};
	selectedInstituteOption?: { value: string; label: string } | null;
}

const initialState: RegisterInstituteFormState = {
	formData: {},
	currentStep: 0,
	currentSubStep: 0,
	maxReachedStep: 0,
	maxReachedSubStep: 0,
	lastSaved: null,
	isDirty: false,
	registerInstituteMode: null,
	isSameAsPrincipal: false,
	selectedLocationOptions: {},
	selectedInstituteOption: null,
};

const registerInstituteFormSlice = createSlice({
	name: "registerInstituteForm",
	initialState,
	reducers: {
		setFormData: (state, action: PayloadAction<Record<string, unknown>>) => {
			state.formData = action.payload;
			state.isDirty = true;
		},
		setCurrentStep: (state, action: PayloadAction<number>) => {
			state.currentStep = action.payload;
			if (action.payload > state.maxReachedStep) {
				state.maxReachedStep = action.payload;
			}
		},
		setCurrentSubStep: (state, action: PayloadAction<number>) => {
			state.currentSubStep = action.payload;
			if (action.payload > state.maxReachedSubStep) {
				state.maxReachedSubStep = action.payload;
			}
		},
		setLastSaved: (state, action: PayloadAction<string>) => {
			state.lastSaved = action.payload;
			state.isDirty = false;
		},
		setRegisterInstituteMode: (
			state,
			action: PayloadAction<(typeof REGISTER_INSTITUTE_MODE)[keyof typeof REGISTER_INSTITUTE_MODE] | null>,
		) => {
			console.log("📝 Form Persistence - Setting registerInstituteMode:", action.payload);
			state.registerInstituteMode = action.payload;
		},
		setSelectedLocationOptions: (
			state,
			action: PayloadAction<{
				nationalAssembly?: { value: string; label: string; uuid?: string } | null;
				provinceAssembly?: { value: string; label: string; uuid?: string } | null;
				unionCouncil?: { value: string; label: string; uuid?: string } | null;
			}>,
		) => {
			state.selectedLocationOptions = {
				...(state.selectedLocationOptions || {}),
				...(action.payload.nationalAssembly !== undefined
					? { nationalAssembly: action.payload.nationalAssembly || undefined }
					: {}),
				...(action.payload.provinceAssembly !== undefined
					? { provinceAssembly: action.payload.provinceAssembly || undefined }
					: {}),
				...(action.payload.unionCouncil !== undefined
					? { unionCouncil: action.payload.unionCouncil || undefined }
					: {}),
			};
		},
		setSelectedInstituteOption: (state, action: PayloadAction<{ value: string; label: string } | null>) => {
			state.selectedInstituteOption = action.payload;
		},
		clearFormData: state => {
			// Preserve important values when clearing form data
			const preservedIsSameAsPrincipal = state.isSameAsPrincipal;
			const preservedOrganizationCategory = state.formData?.organizationCategory;
			const preservedSelectedLocationOptions = state.selectedLocationOptions;
			state.selectedInstituteOption = null;
			state.formData = {};
			// Restore organizationCategory to formData (needed for CNIC section visibility on Attachments step)
			if (preservedOrganizationCategory) {
				state.formData = { organizationCategory: preservedOrganizationCategory };
			}
			state.currentStep = 0;
			state.currentSubStep = 0;
			state.maxReachedStep = 0;
			state.maxReachedSubStep = 0;
			state.lastSaved = null;
			state.isDirty = false;
			state.registerInstituteMode = null;
			state.isSameAsPrincipal = preservedIsSameAsPrincipal; // Preserve the checkbox state
			state.selectedLocationOptions = preservedSelectedLocationOptions;
		},
		resetFormData: state => {
			state.formData = {};
			state.isDirty = false;
			state.selectedLocationOptions = {};
		},
		setIsSameAsPrincipal: (state, action: PayloadAction<boolean>) => {
			state.isSameAsPrincipal = action.payload;
		},
		clearIsSameAsPrincipal: state => {
			state.isSameAsPrincipal = false;
		},
		/** Full wipe for login, logout, or starting a brand-new institute registration. */
		resetRegisterInstituteForm: () => initialState,
	},
});

export const {
	setFormData,
	setCurrentStep,
	setCurrentSubStep,
	setLastSaved,
	setRegisterInstituteMode,
	clearFormData,
	resetFormData,
	setIsSameAsPrincipal,
	clearIsSameAsPrincipal,
	setSelectedLocationOptions,
	setSelectedInstituteOption,
	resetRegisterInstituteForm,
} = registerInstituteFormSlice.actions;

export default registerInstituteFormSlice.reducer;
